import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, debounceTime, distinctUntilChanged, map, startWith, tap } from 'rxjs';
import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { AppState } from 'src/app/core/app.state';
import { ICategory } from 'src/app/features/categories/models/categories.models';
import { FilterDialogComponent } from 'src/app/features/produits/components/filter-dialog/filter-dialog.component';
import { IInventoryFilter, IProduct, IVariant } from 'src/app/features/produits/models/products.models';
import { FetchProducts, SetInventoryFilter, SetInventorySearch, SetProductLoader } from 'src/app/features/produits/store/produits.actions';
import { CommonService } from 'src/app/shared/common.service';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { parseInventoryFilterRequest } from 'src/app/shared/utils/helpers';
import { environment } from 'src/environments/environment';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
import { HasPermissionContext } from 'src/app/shared/has-permission.directive';
@Component({
  selector: 'app-inserting-products',
  templateUrl: './inserting-products.component.html',
  styleUrls: ['./inserting-products.component.scss'],
})
export class InsertingProductsComponent implements OnInit {
  search = new FormControl();
  selectedSearch = new FormControl();
  @Input() selectedProducts0: IProduct[] = [];
  @Input() submitType: string = '';
  selectedProducts: IProduct[] = [];
  filteredSelectedProducts: IProduct[] = [];
  @Output() saveProducts = new EventEmitter<{selectedProducts: IProduct[], missingPricing: boolean}>();
  @Output() cancelProducts = new EventEmitter<string>();
  expandedIndex: number = -1;
  products$: Observable<IProduct[]> = this.store.select((state)=> state.products.products);
  private subscriptions: Subscription[] = [];
  products: IProduct[] = [];
  allProducts: IProduct[] = [];
  filter: any | undefined = undefined;
  selectedProductsfilter: any | undefined = undefined;
  isProductsFiltered: boolean = false;
  isSelectedFiltered: boolean = false;
  filterType: string = '';
  placeholder: string = 'assets/images/placeholder.png';
  checkedProducts: IProduct[] = [];
  checkSelectedProducts: IProduct[] = [];
  pagination$: Observable<IPagination | null> = this.store.select( (state) => state.products.metadata );
  page: IPageCount = {
      size: DEFAULT_PAGE_SIZE,
      number: 1
  }
  key: string | undefined = undefined;
  baseUrl = environment.BASE_URL_API;
  isAppLoading$ = this.store.select((state) => state.shared.isAppLoading);
  canSubmit = false;
  constructor(
    private route: Router,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.selectedProducts = [...this.selectedProducts0];
    this.filteredSelectedProducts = [...this.selectedProducts0]
    const sub1 = this.products$.subscribe(products => {
      this.allProducts = products
      this.products = this.productFilterHelper();
    });

    const sub2 = this.search.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap(term => this.store.dispatch(SetInventorySearch({ keyword: term! })))
    ).subscribe()

    const sub3 = this.selectedSearch.valueChanges.pipe(
      startWith(''),
      map(selectedSearch => selectedSearch.toLowerCase())
    ).subscribe(selectedSearch => {
      this.filterProducts('selectedProductsList');
    })

    // Handle all products filter using the api
    const sub4 = this.store
    .select((state) => state.products.productFilterState)
    .subscribe((filter) => {
      this.key = filter.keyword;
      this.filter = parseInventoryFilterRequest(filter.advanced);

      const isFiltered = this.isFiltered(filter.advanced, 'allProductsList');
      this.isProductsFiltered = isFiltered;
      if(isFiltered){
        this.filterType = 'allProductsList';
      }
      this.store.dispatch(
        FetchProducts({ page: {...this.page, number: 1 }, filter: this.filter, key: filter.keyword })
      );
    });

    const action = this.submitType === 'stepper' ? 'create' : 'edit';
    const sub5 = this.store.select(selectCanDo('catalog', action)).subscribe((canSubmit) => {
      this.canSubmit = canSubmit;
    });

    this.subscriptions.push(sub1, sub2, sub3, sub4, sub5);
  }

  setFilter(filterType: string) {
    this.filterType = filterType;
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      minWidth: '650px',
      width: '90vw',
      height: '80vh',
      data: filterType === 'allProductsList' ? null : {
        filter: this.selectedProductsfilter
      }
    });

    // Handle selected products filter locally
    dialogRef.afterClosed().subscribe((filter: IInventoryFilter) => {
      if(filter) {
        const isFiltered = this.isFiltered(filter, filterType);
        if(this.filterType === 'selectedProductsList'){
          this.isSelectedFiltered = isFiltered;
          this.filterProducts('selectedProductsList');
        }
      }
      if(filter && this.filterType !== 'selectedProductsList'){
        this.store.dispatch(SetInventoryFilter({keyword: this.key ?? '', advanced: filter}))
      }
    })
  }

  isFiltered(filter: any, type: string) {
    if(filter) {
      const parsedFilter = {...filter.inventoryFilter, ...filter.productsInfoFilter, ...filter.organizeFilter};
      if(type === 'selectedProductsList') this.selectedProductsfilter = parsedFilter;

      const isFiltered = !!(
        (parsedFilter.name && parsedFilter.name !== "") ||
        (parsedFilter.sku && parsedFilter.sku !== "") ||
        (parsedFilter.barcode && parsedFilter.barcode !== "") ||
        (parsedFilter.brands && parsedFilter.brands.length > 0) ||
        parsedFilter.status ||
        parsedFilter.has_variants ||
        parsedFilter.sales_price_min != null ||
        parsedFilter.sales_price_max != null ||
        parsedFilter.cost_min != null ||
        parsedFilter.cost_max != null ||
        parsedFilter.quantity_min != null ||
        parsedFilter.quantity_max != null ||
        parsedFilter.reorder_point_min != null ||
        parsedFilter.reorder_point_max != null ||
        (parsedFilter.categories && parsedFilter.categories.length > 0) ||
        (parsedFilter.tags && parsedFilter.tags.length > 0) ||
        parsedFilter.has_catalogs ||
        parsedFilter.number_of_catalogs != null
      );

      return isFiltered
    }

    return false;
  }

  checkAllProducts(type: string) {
    if (!this.canSubmit) return;
    if(type === 'allProductsList') {
      if (this.isAllChecked('allProductsList')) {
        this.checkedProducts = []; // Uncheck all
      } else {
        this.checkedProducts = [...this.products]; // Check all
      }
    }
    if(type === 'selectedProductsList') {
      if (this.isAllChecked('selectedProductsList')) {
        this.checkSelectedProducts = []; // Uncheck all
      } else {
        this.checkSelectedProducts = [...this.filteredSelectedProducts]; // Check all
      }
    }
  }

  checkProduct(product: IProduct, event: any, type: string) {
    if (!this.canSubmit) return;
    if(type === 'selectedProductsList') {
      this.checkSelectedProducts = event.checked ? [...this.checkSelectedProducts, product] : this.checkSelectedProducts.filter(p => p.id !== product.id);
      console.log('Check Selected Product', product, this.checkSelectedProducts)
    }
    if(type === 'allProductsList'){
      this.checkedProducts = event.checked ? [...this.checkedProducts, product] : this.checkedProducts.filter(p => p.id !== product.id);
      console.log('Check Product', product, this.checkedProducts)
    }
  }

  isProductChecked(productId: number, type: string): boolean {
    if(type === 'allProductsList') {
      return this.checkedProducts.some(p => p.id === productId);
    }
    if(type === 'selectedProductsList') {
      return this.checkSelectedProducts.some(p => p.id === productId);
    }
    return false
  }

  isAllChecked(type: string): boolean {
    if(type === 'selectedProductsList') {
      return this.filteredSelectedProducts.length > 0 && this.filteredSelectedProducts.every(p => this.checkSelectedProducts.some(cp => cp.id === p.id));
    }
    if(type === 'allProductsList') {
      return this.products.filter(p => !(!p.variants && (!p.sales_price || p.sales_price <= 0 ) )).length > 0 && this.products.filter(p => !(!p.variants && (!p.sales_price || p.sales_price <= 0 ) )).every(p => this.checkedProducts.some(cp => cp.id === p.id));
    }
    return false
  }

  isAllCheckedDisabled(type: string): boolean {
    if(type === 'selectedProductsList') {
      return this.filteredSelectedProducts.length === 0;
    }
    if(type === 'allProductsList') {
      return this.products.filter(p => !(!p.variants && (!p.sales_price || p.sales_price <= 0 ) )).length === 0;
    }
    return false
  }

  addSelectedProducts(type: string): boolean | void{
    if (!this.canSubmit) return;
    if(type === 'selectedProductsList') {
      if(this.checkSelectedProducts.length === 0) return false
      this.checkSelectedProducts.forEach(product => {
        this.removeProduct(product) // to refactor
      })
    }
    if(type === 'allProductsList'){
      if(this.checkedProducts.length === 0) return false
      this.checkedProducts.forEach(product => {
        // Ensure product has valid SKU before adding
        // If product SKU is missing or "0", try to get it from variants
        if ((!product.sku || product.sku === '0') && product.variants && product.variants.length > 0) {
          // Try to get SKU from first variant that has a valid SKU
          const variantWithSku = product.variants.find(v => v.sku && v.sku !== '0');
          if (variantWithSku && variantWithSku.sku) {
            product = { ...product, sku: variantWithSku.sku };
          }
        }
        this.addProduct(product) // to refactor
      })
    }
    this.checkedProducts = [];
    this.checkSelectedProducts = [];
  }

  filterProducts(type: string): void {
    if(type === 'allProductsList') {
      this.products = this.productFilterHelper()
      this.checkedProducts = this.products.filter(p => this.checkedProducts.some(cp => cp.id === p.id));
    }
    if(type === 'selectedProductsList') {
      if(this.isSelectedFiltered && this.filterType === 'selectedProductsList') {
        this.filteredSelectedProducts = this.applyFilter(type)
      }
      else {
        this.filteredSelectedProducts = this.selectedProductsFilterHelper();
      };
      this.checkSelectedProducts = this.filteredSelectedProducts.filter(p => this.checkSelectedProducts.some(cp => cp.id === p.id));
    }
  }

  clearFilter(key: string) {
    if(key === 'allProductsList'){
      this.isProductsFiltered = false
      this.store.dispatch(SetInventoryFilter({ keyword: this.key ?? '', advanced: null }));
    } else if (key === 'selectedProductsList'){
      this.isSelectedFiltered = false
      this.selectedProductsfilter = null
      this.filterProducts('selectedProductsList')
    }
  }

  toggleVariants(id: number) {
    this.expandedIndex = this.expandedIndex === id ? -1 : id;
  }

  addVariantsProduct(product: IProduct, variant_id: number) {
    if (!this.canSubmit) return;
    const isProductExist = this.selectedProducts.some(selectedProduct => selectedProduct.id === product.id);
    
    // Find the variant to get its SKU
    const variant = product.variants?.find(v => v.id === variant_id);
    
    // Helper to get valid SKU (not 0, not empty)
    const getValidSku = (productSku: string | undefined, variantSku: string | undefined): string => {
      // If product SKU is valid (exists and not '0'), use it
      if (productSku && productSku !== '0') {
        return productSku;
      }
      // Otherwise, use variant SKU if available
      if (variantSku && variantSku !== '0') {
        return variantSku;
      }
      // Fallback to product SKU or empty string
      return productSku || '';
    };
    
    if(!isProductExist){
      // Use variant SKU if product SKU is missing or invalid
      const productSku = getValidSku(product.sku, variant?.sku);
      
      this.selectedProducts.push({
        ...product,
        sku: productSku, // Ensure SKU is set
        variants: product.variants?.filter(variant => variant.id === variant_id)
      });
    }else{
      this.selectedProducts = this.selectedProducts.map(selectedProduct => {
        if(selectedProduct.id === product.id){
          // Use variant SKU if product SKU is missing or invalid
          const productSku = getValidSku(selectedProduct.sku, variant?.sku);
          
          return {
            ...selectedProduct,
            sku: productSku, // Ensure SKU is preserved
            variants: [
              ...selectedProduct.variants!,
              ...product.variants?.filter(variant => variant.id === variant_id)!
            ]
          }
        }
        return selectedProduct;
      })
    }
    this.filterProducts('allProductsList');
    this.filterProducts('selectedProductsList');
  }

  removeVariantsProduct(product: IProduct, variant_id: number){
    if (!this.canSubmit) return;
    this.selectedProducts = this.selectedProducts
      .map(selectedProduct => {
        if (selectedProduct.id === product.id) {
          if (selectedProduct.variants?.length === 1) {
            return null;
          } else {
            return {
              ...selectedProduct,
              variants: selectedProduct.variants?.filter(variant => variant.id !== variant_id),
            };
          }
        }
        return selectedProduct;
      })
      .filter(product => product !== null) as IProduct[];

    this.filterProducts('allProductsList');
    this.filterProducts('selectedProductsList');
  }

  addProduct(product: IProduct): void {
    if (!this.canSubmit) return;
    if(product.variants && product.variants.length > 0){
      product.variants.forEach(variant => {
        this.addVariantsProduct(product, variant.id) // to refactor
      })
    } else {
      // Ensure SKU is properly set - check if it's valid (not '0', not empty)
      // If product SKU is missing or invalid, try to get it from the first variant if available
      let productSku = product.sku;
      if (!productSku || productSku === '0') {
        // Try to get SKU from variants if product has any
        if (product.variants && product.variants.length > 0 && product.variants[0]?.sku && product.variants[0].sku !== '0') {
          productSku = product.variants[0].sku;
        } else {
          productSku = product.sku || ''; // Keep original or empty string
        }
      }
      
      const productWithSku = {
        ...product,
        sku: productSku // Ensure SKU field is properly set
      };
      this.selectedProducts.push(productWithSku);
      this.filterProducts('allProductsList');
      this.filterProducts('selectedProductsList');
    }
  }

  removeProduct(product: IProduct): void {
    if (!this.canSubmit) return;
    if(product.variants && product.variants.length > 0){
      product.variants.forEach(variant => {
        this.removeVariantsProduct(product, variant.id) // to refactor
      })
    } else {
      this.selectedProducts = this.selectedProducts.filter(p => p.id !== product.id);
      this.filterProducts('allProductsList');
      this.filterProducts('selectedProductsList');
    }
  }

  drop(event: CdkDragDrop<IProduct[]>) {
    if (!this.canSubmit) return;
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      if(!event.item.data || !event.item.data.product)
        return
      if(!event.item.data.selected){
        if(event.item.data.variant_id)
          this.addVariantsProduct(event.item.data.product, event.item.data.variant_id)
        else
          this.addProduct(event.item.data.product);
      }
      if(event.item.data.selected){
        if(event.item.data.variant_id)
          this.removeVariantsProduct(event.item.data.product, event.item.data.variant_id)
        else
          this.removeProduct(event.item.data.product);
      }
    }
  }

  trackByFn(index: number, item: IProduct) {
    return item.id;
  }

  getProductsWithoutPricingCount() {
    let uniqueProducts: (IProduct | IVariant)[] = []
    this.selectedProducts.map((product) => {
      const productDetail = product.variants?.length ? product.variants : [product];

      uniqueProducts = [...uniqueProducts, ...productDetail]
    })

    return uniqueProducts.filter((product: (IProduct | IVariant)) => product.final_price == null && product.sales_price == null).length
  }

  save(): boolean | void {
    if (!this.canSubmit) return;
    console.log('Next', this.selectedProducts)
    if(this.selectedProducts.length === 0){
      this.commonService.showToast(
        this.translateService.instant('Catalogs.selectedAtLeastProduct'),
        'WARNING'
      );
      return false
    }
    this.setInitRuels();
    this.saveProducts.emit({selectedProducts: this.selectedProducts, missingPricing: this.getProductsWithoutPricingCount() > 0 });
  }

  setInitRuels(): void{
    this.selectedProducts = this.selectedProducts.map((product: IProduct) => {
      return {
        ...product,
        init_increment: product.increment,
        init_min_qte: product.min_qte,
        init_max_qte: product.max_qte,
        variants: (product.variants && product.variants.length > 0 ) ? product.variants.map((variant: IVariant) => {
          return {
            ...variant,
            init_increment: variant.increment,
            init_min_qte: variant.min_qte,
            init_max_qte: variant.max_qte,
          }
        }) : []
      }
    });
  }

  cancel(){
    const submitType = this.submitType === 'stepper' ? 'stepper' : 'summary';
    this.cancelProducts.emit(submitType)
    this.store.dispatch(SetInventorySearch({ keyword: '' }));
  }

  applyFilter(type: string): IProduct[] {
    let products: IProduct[] = [];
    const filter = this.selectedProductsfilter;
    products = this.selectedProductsFilterHelper();

    return products.filter(product =>
        (filter.name === undefined || product.name?.toLowerCase().includes(filter.name.toLowerCase())) &&
        (filter.sku === undefined || product.sku?.toLowerCase().includes(filter.sku.toLowerCase())) &&
        (filter.barcode === undefined || product.barcode?.toString().includes(filter.barcode.toString())) &&
        (filter.brands === undefined || filter.brands.length === 0 || filter.brands.some((brand: any) => brand.id === product.brand?.id)) &&
        (filter.status === undefined || product.status === filter.status) &&
        (filter.has_variants === undefined ||
          (product.variants?.length! > 0 && filter.has_variants === 'true') ||
          (!product.variants?.length && filter.has_variants === 'false')) &&
        (filter.sales_price_min === undefined || product.sales_price >= filter.sales_price_min) &&
        (filter.sales_price_max === undefined || product.sales_price <= filter.sales_price_max) &&
        (filter.cost_min === undefined || product.cost >= filter.cost_min) &&
        (filter.cost_max === undefined || product.cost <= filter.cost_max) &&
        (filter.quantity_min === undefined || (product.stock?.quantity ?? 0) >= filter.quantity_min) &&
        (filter.quantity_max === undefined || (product.stock?.quantity ?? 0) <= filter.quantity_max) &&
        (filter.reorder_point_min === undefined || (product.stock?.reorder_point ?? 0) >= filter.reorder_point_min) &&
        (filter.reorder_point_max === undefined || (product.stock?.reorder_point ?? 0) <= filter.reorder_point_max) &&
        (filter.categories === undefined || filter.categories.length === 0 || product.categories?.some(cat => filter.categories.map((category: ICategory) => category.id).includes(cat.id))) &&
        (filter.tags === undefined || filter.tags.length === 0 || product.tags?.some(tag => filter.tags.some((fTag: any) => fTag.id === tag.id))) &&
        (filter.has_catalogs === undefined || (product.catalogs?.length! > 0) === filter.has_catalogs) &&
        (filter.number_of_catalogs === undefined || product.catalogs?.length === filter.number_of_catalogs)
    );
  }


  productFilterHelper():IProduct[]{
    const search = this.search.value ? this.search.value.toLowerCase() : '';
    return this.allProducts
      .map(product => {
        // Check if the product has variants and if it exists in selectedProducts
        const selectedProduct = this.selectedProducts.find(selected => selected.id === product.id);

        if (product.variants && product.variants.length > 0 && selectedProduct) {
          // Filter variants to keep only those that exist in the selectedProduct's variants
          const selectedVariantIds = new Set(selectedProduct.variants?.map(v => v.id) || []);
          if(selectedVariantIds.size === product.variants.length){
            return null
          }
          const filteredVariants = product.variants.filter(variant => !selectedVariantIds.has(variant.id));

          return { ...product, variants: filteredVariants };
        }

        // Apply normal filtering for products without variants
        if (
          !this.selectedProducts.some(selectedProduct => selectedProduct.id === product.id)
          // &&
          // (product.name?.toLowerCase().includes(search) ||
          //   product.sku?.toLowerCase().includes(search) ||
          //   product.brand?.name.toLowerCase().includes(search) ||
          //   product.category?.name.toLowerCase().includes(search))
        ) {
          return product;
        }

        return null; // If the product does not match, return null
      })
      .filter((product): product is IProduct => product !== null); // Remove null values with a type predicate

  }

  selectedProductsFilterHelper():IProduct[]{
    const searchTerm = this.selectedSearch.value ? this.selectedSearch.value?.toLowerCase() : '';
    return this.selectedProducts.filter(product =>
        (product.name?.toLowerCase().includes(searchTerm) ?? false) ||
        (product.sku?.toLowerCase().includes(searchTerm) ?? false) ||
        ((product.brand && product.brand.name && product.brand.name?.toLowerCase().includes(searchTerm)) ?? false) /* ||
        ((product.category && product.category.name && product.category.name?.toLowerCase().includes(searchTerm)) ?? false) */
    );
  }

  toPage(page: IPageCount) {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.store.dispatch(
      FetchProducts({ page: this.page, filter: this.filter, key: this.key })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub => sub.unsubscribe()))
    this.store.dispatch(SetInventoryFilter({ keyword: '', advanced: null }));
  }

  submitPermission(): HasPermissionContext {
    return { module: 'catalog', action: this.submitType === 'stepper' ? 'create' : 'edit' };
  }
}

